import SearchIcon from '@mui/icons-material/Search';
import { Box, Grid, InputBase } from '@mui/material';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { debounce, isEmpty } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { ClientContactDTO } from 'wisestep-api';
import {
  WsButton,
  WsDataGrid,
  WsIconNoUserGeneric,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { warning } from '../../../utils/NotificationMessages';
import { INotification } from '../../../utils/NotificationStore';
import { ContactFormValues, CrmStyledFooter } from '../helper/crmJobHelper';

interface ContactCheckboxListProps {
  contacts: ClientContactDTO[];
  isTagged: boolean;
  methods: UseFormReturn<ContactFormValues>;
  onAssign: () => void;
  onSelectAll: (checked: boolean, list: ClientContactDTO[]) => void;
  reviewContactCount?: number;
  setNotification?: (notification: INotification) => void;
  assignedContactsCount?: number;
}

const CrmContactCheckboxList = ({
  contacts,
  isTagged,
  methods,
  onAssign,
  onSelectAll,
  reviewContactCount,
  setNotification,
  assignedContactsCount = 0,
}: ContactCheckboxListProps) => {
  const { watch, setValue } = methods;
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = debounce((value: string) => setSearchTerm(value.toLowerCase()));

  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      const fullText =
        `${contact.firstName} ${contact.lastName} ${contact.emailAddress} ${contact.position}`.toLowerCase();
      return fullText.includes(searchTerm);
    });
  }, [contacts, searchTerm]);

  const selectedIds = watch('contactList') ?? [];

  const getAvailableSlots = (): number => {
    if (reviewContactCount === undefined) return 0;
    return reviewContactCount - assignedContactsCount;
  };

  const showWarningIfExceedsLimit = (count: number): boolean => {
    if (!isTagged && reviewContactCount && reviewContactCount > 0) {
      const availableSlots = getAvailableSlots();
      if (count > availableSlots) {
        setNotification?.(
          warning(
            `You can only select up to ${availableSlots} more contacts for review. ${assignedContactsCount} contacts are already assigned.`
          )
        );
        return true;
      }
    }
    return false;
  };

  const handleSelectionChange = (selection: GridSelectionModel) => {
    const safeSelection = (selection as string[]).filter((id): id is string => id !== null);
    if (showWarningIfExceedsLimit(safeSelection.length)) return;
    setValue('contactList', safeSelection);
  };

  const handleSelectAllChange = (checked: boolean) => {
    const allIds = filteredContacts.map((c) => c.id).filter((id): id is string => !!id);
    if (checked && showWarningIfExceedsLimit(allIds.length)) return;
    setValue('contactList', checked ? allIds : []);
    onSelectAll(checked, filteredContacts);
  };

  const rows = useMemo(() => {
    return filteredContacts.map((c) => ({
      id: c.id!,
      firstName: c.firstName,
      lastName: c.lastName,
      emailAddress: c.emailAddress ?? 'N/A',
      position: c.position ?? 'N/A',
      owner: c.owner?.name ?? 'N/A',
      location: !isEmpty(c.location)
        ? [c.location?.city, c.location?.state, c.location?.country].filter(Boolean).join(', ')
        : '',
    }));
  }, [filteredContacts]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: ({ row }) => `${row.firstName} ${row.lastName}`,
    },
    {
      field: 'emailAddress',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: 1,
    },
    {
      field: 'owner',
      headerName: 'Contact Owner',
      flex: 0.8,
    },
    {
      field: 'location',
      headerName: 'Location',
      sortable: false,
      flex: 0.7,
      renderCell: (params) => (
        <WsTypography hoverText={params.row['location']}>{params.row['location']}</WsTypography>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      {isEmpty(rows) ? (
        <NoDataFoundCard
          text={`No ${isTagged ? 'Mailing' : 'Eligible'} contacts`}
          icon={<WsIconNoUserGeneric />}
        />
      ) : (
        <Box>
          <Grid container direction="row" spacing={1} alignItems="center" mb={1}>
            <Grid item>
              <SearchIcon />
            </Grid>
            <Grid item flexGrow={1}>
              <InputBase
                placeholder="Search Contacts"
                fullWidth
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </Grid>
          </Grid>
          <WsCheckBox
            name="selectAll"
            label="Select All"
            bold={true}
            onClick={(checked) => handleSelectAllChange(checked)}
          />
          <Box sx={{ height: 'calc(100vh - 340px)', overflowY: 'auto' }}>
            <WsDataGrid
              columns={columns}
              rows={rows}
              checkboxSelection
              selectionModel={selectedIds}
              onSelectionModelChange={handleSelectionChange}
              autoHeight
              paginationMode="client"
              columnVisibility={false}
            />
          </Box>
          <CrmStyledFooter>
            <Grid container justifyContent="space-between" my={3}>
              <Grid item>
                <WsButton variant="text" size="large">
                  <Box display="flex" alignItems="center">
                    {`${selectedIds?.length || 0} Selected`}
                    {!isTagged && reviewContactCount && reviewContactCount > 0 && (
                      <WsTypography color={WsTypoColors.PRIMARY}>
                        (Max: {reviewContactCount - assignedContactsCount})
                      </WsTypography>
                    )}
                  </Box>
                </WsButton>
              </Grid>
              <Grid item pr={2}>
                <WsButton variant="contained" size="large" onClick={onAssign}>
                  {isTagged ? 'Unassign' : 'Assign'}
                </WsButton>
              </Grid>
            </Grid>
          </CrmStyledFooter>
        </Box>
      )}
    </FormProvider>
  );
};

export default CrmContactCheckboxList;
